/**
 * @module Routes.Unsubscribe
 *
 */
import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { defineMessages } from 'react-intl';
import { RootState } from 'app/reducers';
import { cls } from 'app/utils';
import { UserSettingsModel } from 'app/models';
import { fetchUserSettings, saveUserSettings } from 'app/actions/user-settings';
import { useActions, useLoc } from 'app/utils/hooks';
import { Loc, Toast } from 'app/components/helpers';
import { Public } from 'app/components/Wrappers';
import CheckBox from 'app/components/inputs/CheckBox';
import Styles from './styles.module.scss';

type PropsDef = RouteComponentProps<{
  token?: string;
}>;

interface State {
  pendingInvitationsEmails: boolean;
  dailyDigestEmails: boolean;
  morningDelivery: boolean;
  afternoonDelivery: boolean;
  eveningDelivery: boolean;
  weeklyDigestEmails: boolean;
  ready: boolean;
  success: string;
  error: string;
}

const initState: State = {
  pendingInvitationsEmails: false,
  dailyDigestEmails: false,
  morningDelivery: false,
  afternoonDelivery: false,
  eveningDelivery: false,
  weeklyDigestEmails: false,
  ready: false,
  success: null,
  error: null,
};

/**
 * @class Usergt
 *
 */
export default function User(props: PropsDef) {
  const token = props.match.params.token;
  const { formatMessage } = useLoc();
  const [ state, setState ] = useState(initState);

  const userSettings = useSelector((state: RootState) => state.userSettings);
  const actions = useActions({ fetchUserSettings, saveUserSettings });

  useEffect(() => (actions.fetchUserSettings(token), undefined), []);
  useEffect(() => {
    if (userSettings.fetch.data != null) {
      const [ morningDelivery, afternoonDelivery, eveningDelivery ] = userSettings.fetch.data.dailyDigestEmailsDeliveryTimeDenormalized;
      setState({
        ...state, morningDelivery, afternoonDelivery, eveningDelivery,
        pendingInvitationsEmails: userSettings.fetch.data.pendingInvitationsEmails,
        dailyDigestEmails: userSettings.fetch.data.dailyDigestEmails,
        weeklyDigestEmails: userSettings.fetch.data.weeklyDigestEmails,
        ready: true,
      });
    }
  }, [ userSettings.fetch.data ]);

  const saveSettings = () => {
    const dailyDeliveries = ([
      state.morningDelivery ? 'morning' : null,
      state.afternoonDelivery ? 'afternoon' : null,
      state.eveningDelivery ? 'evening' : null,
    ]).filter(item => item != null);

    const settings: Partial<UserSettingsModel> = {
      dailyDigestEmails: state.dailyDigestEmails,
      weeklyDigestEmails: state.weeklyDigestEmails,
      pendingInvitationsEmails: state.pendingInvitationsEmails,
      dailyDigestEmailsDeliveryTime: dailyDeliveries,
      dailyDigestEmailsDeliveryTimeDenormalized: [
        state.morningDelivery,
        state.afternoonDelivery,
        state.eveningDelivery
      ]
    };

    actions.saveUserSettings(settings as UserSettingsModel, token)
      .then(() => setState({ ...state, success: formatMessage(locales.success_message) }))
      .catch(() => setState({ ...state, error: formatMessage(locales.error_message) }));
  };

  const handleChange = (value: boolean, name: string) => {
    setState({ ...state, [name]: value });
  };

  let buttonText = (<Loc value={locales.button_save} />);
  if (userSettings.save.pending) {
    buttonText = (<Loc value={locales.button_wait} />);
  }

  function renderDaily() {
    if (state.dailyDigestEmails) {
      return (
        <div className={cls(Styles.section, Styles.daily)}>
          <div className={Styles.settings}>
            <CheckBox
              className={Styles.checkbox}
              checked={state.morningDelivery}
              label={formatMessage(locales.digest_delivery_morning)}
              onChange={(e) => handleChange(e, 'morningDelivery')}
            />
            <CheckBox
              className={Styles.checkbox}
              label={formatMessage(locales.digest_delivery_afternoon)}
              checked={state.afternoonDelivery}
              onChange={(e) => handleChange(e, 'afternoonDelivery')}
            />
            <CheckBox
              className={Styles.checkbox}
              label={formatMessage(locales.digest_delivery_evening)}
              checked={state.eveningDelivery}
              onChange={(e) => handleChange(e, 'eveningDelivery')}
            />
          </div>
        </div>
      );
    }
    return (<div />);
  }

  if (userSettings.fetch.error != null) {
    return (
      <Public>
        <div className="panel">
          <h3 className="text-danger text-center">
            <Loc value={locales.bad_token} />
          </h3>
        </div>
      </Public>
    );
  }

  return (
    <Public isLoading={!state.ready}>
      <Toast
        success={state.success}
        error={state.error}
        onDone={() => setState({ ...state, error: null, success: null })}
      />

      <div className={cls(Styles.unsubscribeMain, Styles.user)}>
        <div className={Styles.section}>
          <div className={Styles.header}><Loc value={locales.title} /></div>

          <div className={Styles.settings}>
            <CheckBox
              className={Styles.checkbox}
              label={formatMessage(locales.weekly_reminder)}
              checked={state.pendingInvitationsEmails}
              onChange={val => handleChange(val, 'pendingInvitationsEmails')}
            />
          </div>
        </div>

        <div className={Styles.section}>
          <div className={Styles.header}><Loc value={locales.daily_section} /></div>

          <div className={Styles.settings}>
            <CheckBox
              className={Styles.checkbox}
              label={formatMessage(locales.daily_digest)}
              checked={state.dailyDigestEmails}
              onChange={val => handleChange(val, 'dailyDigestEmails')}
            />

            {renderDaily()}
          </div>
        </div>

        <div className={Styles.section}>
          <div className={Styles.header}><Loc value={locales.weekly_section} /></div>

          <div className={Styles.settings}>
            <CheckBox
              className={Styles.checkbox}
              label={formatMessage(locales.weekly_digest)}
              checked={state.weeklyDigestEmails}
              onChange={val => handleChange(val, 'weeklyDigestEmails')}
            />
          </div>
        </div>

        <div className={Styles.buttonBar}>
          <button
            className="btn btn-primary"
            onClick={saveSettings}
            disabled={userSettings.save.pending}
          >{buttonText}</button>
        </div>
      </div>
    </Public>
  );
}


const locales = defineMessages({
  button_save: { id: 'unsubscribe.user.save', defaultMessage: 'Save settings' },
  button_wait: { id: 'unsubscribe.user.wait', defaultMessage: 'Please wait...' },

  title: { id: 'unsubscribe.user.title', defaultMessage: 'Subscriptions' },
  daily_section: { id: 'unsubscribe.user.daily_section', defaultMessage: 'Daily digest emails' },
  weekly_section: { id: 'unsubscribe.user.weekly_section', defaultMessage: 'Weekly digest emails' },

  weekly_reminder: { id: 'unsubscribe.user.weekly_reminder', defaultMessage: 'I want to receive weekly pending invitations reminder' },
  daily_digest: { id: 'unsubscribe.user.daily_digest', defaultMessage: 'I want to receive daily digest emails' },
  digest_delivery_morning: { id: 'unsubscribe.user.digest_delivery_morning', defaultMessage: 'Morning (around 8am)' },
  digest_delivery_afternoon: { id: 'unsubscribe.user.digest_delivery_afternoon', defaultMessage: 'Afternoon (around 2pm)' },
  digest_delivery_evening: { id: 'unsubscribe.user.digest_delivery_evening', defaultMessage: 'Evening (around 6pm)' },
  weekly_digest: { id: 'unsubscribe.user.weekly_digest', defaultMessage: 'I want to receive weekly digest emails' },

  success_message: { id: 'unsubscribe.user.success', defaultMessage: 'Your settings have been saved!' },
  error_message: { id: 'unsubscribe.user.error', defaultMessage: 'Due to server error we were not able to save your changes' },
  bad_token: { id: 'unsubscribe.user.bad_token', defaultMessage: 'The access token provided is not valid, or the user does not exists.' },
});


